/**
 * Carry ad attribution from Shopify landers to the funnel on
 * go.tailored-health.com, and to the app on my.tailored-health.com.
 *
 * Ads land on Shopify with ?gclid=… (Google auto-tagging), ?fbclid=… (Meta)
 * and usually utm_* params. Without this hop, the cross-domain link drops all
 * of it and offline / CAPI conversion upload has nothing to attribute.
 *
 * What gets appended:
 *  - Click IDs (gclid / wbraid / gbraid / fbclid), each stored with the time
 *    it was first seen and expiring 90 days after that.
 *  - fbc, built from the stored fbclid as fb.1.<click ms>.<fbclid>. The _fbc
 *    cookie is only used when it already matches that fbclid: on a landing
 *    this script runs before the Meta pixel refreshes the cookie, so reading
 *    it early ships the previous visit's click.
 *  - fbp, read from the _fbp cookie at click time.
 *  - th_last_touch=google|meta when both platforms have a live click ID, the
 *    more recent click wins (also sent when only one is present).
 *  - The landing visit's utm_* params. The utm_* already on a CTA href are
 *    page defaults, used only when the visit arrived untagged.
 *  - th_cta: the CTA placement. Templates set it on the href; older hrefs that
 *    still put the placement in utm_content are converted.
 *
 * Funnel CTAs (go.tailored-health.com/start, or a campaign's landing page
 * there) get everything, and so do links still pointing at the retired
 * my.tailored-health.com/labs-get-started, which Rails sends on to /start
 * with its query string. Other app links (header "Log in", logo, etc.) get
 * click IDs only, so a signup that starts there still carries the gclid.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'th_ad_attribution';
  // Earlier formats ({ ids, expiry }) — still read so in-flight click IDs
  // survive the upgrade.
  var LEGACY_STORAGE_KEYS = ['th_ad_click_ids', 'th_google_click_ids'];
  var TTL_MS = 90 * 24 * 60 * 60 * 1000;
  var GOOGLE_KEYS = ['gclid', 'wbraid', 'gbraid'];
  var META_KEYS = ['fbclid'];
  var CLICK_KEYS = GOOGLE_KEYS.concat(META_KEYS);
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  // What a Google click ID implies when the ad itself carried no UTMs.
  var GOOGLE_INFERRED_UTM = { utm_source: 'google', utm_medium: 'cpc' };
  var APP_HOST = 'my.tailored-health.com';
  var FUNNEL_HOST = 'go.tailored-health.com';
  var LEGACY_FUNNEL_PATH = '/labs-get-started';
  var ORIGINAL_HREF_ATTR = 'data-th-href';

  function emptyState() {
    return { v: 2, clicks: {}, utm: null };
  }

  function isLive(entry, now) {
    return !!(entry && entry.ts && now - entry.ts < TTL_MS);
  }

  function readLegacy() {
    var state = emptyState();
    for (var i = 0; i < LEGACY_STORAGE_KEYS.length; i++) {
      try {
        var raw = window.localStorage.getItem(LEGACY_STORAGE_KEYS[i]);
        if (!raw) continue;
        var parsed = JSON.parse(raw);
        if (!parsed || !parsed.ids || !parsed.expiry) continue;
        // Old format only kept the last write time; best available estimate of
        // when the click happened.
        var ts = parsed.expiry - TTL_MS;
        CLICK_KEYS.forEach(function (key) {
          if (parsed.ids[key] && !state.clicks[key]) {
            state.clicks[key] = { value: parsed.ids[key], ts: ts };
          }
        });
      } catch (e) {
        /* ignore */
      }
    }
    return state;
  }

  function readState() {
    var state = null;
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) state = JSON.parse(raw);
    } catch (e) {
      state = null;
    }
    if (!state || state.v !== 2) state = readLegacy();
    state.clicks = state.clicks || {};

    var now = Date.now();
    Object.keys(state.clicks).forEach(function (key) {
      if (!isLive(state.clicks[key], now)) delete state.clicks[key];
    });
    if (!isLive(state.utm, now)) state.utm = null;
    return state;
  }

  function writeState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      LEGACY_STORAGE_KEYS.forEach(function (key) {
        window.localStorage.removeItem(key);
      });
    } catch (e) {
      /* private mode / quota — in-memory state still covers this pageview */
    }
  }

  function cookieValue(name) {
    try {
      var match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Fold the current URL into stored state. A tagged landing replaces the
   * stored UTMs: explicit utm_* win, a bare Google click ID implies
   * google / cpc, and a bare fbclid (also present on organic Facebook shares)
   * just clears the previous visit's UTMs.
   */
  function captureLanding(state) {
    var params = new URLSearchParams(window.location.search);
    var now = Date.now();
    var sawGoogle = false;
    var sawClick = false;

    CLICK_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (!value) return;
      sawClick = true;
      if (GOOGLE_KEYS.indexOf(key) !== -1) sawGoogle = true;
      var existing = state.clicks[key];
      // Same ID again (reload, back button) keeps its original click time.
      if (!existing || existing.value !== value) {
        state.clicks[key] = { value: value, ts: now };
      }
    });

    var utm = {};
    var sawUtm = false;
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        utm[key] = value;
        sawUtm = true;
      }
    });

    if (sawUtm) {
      state.utm = { params: utm, inferred: false, ts: now };
    } else if (sawGoogle) {
      state.utm = { params: GOOGLE_INFERRED_UTM, inferred: true, ts: now };
    } else if (sawClick) {
      state.utm = null;
    }
  }

  function latestTs(state, keys) {
    var latest = 0;
    keys.forEach(function (key) {
      var entry = state.clicks[key];
      if (entry && entry.ts > latest) latest = entry.ts;
    });
    return latest;
  }

  /** Click IDs + Meta browser params + last-touch, resolved at call time. */
  function clickParams(state) {
    var out = {};
    CLICK_KEYS.forEach(function (key) {
      if (state.clicks[key]) out[key] = state.clicks[key].value;
    });

    var fbclid = state.clicks.fbclid;
    var fbcCookie = cookieValue('_fbc');
    if (fbclid) {
      var matchesCookie = fbcCookie && fbcCookie.slice(-(fbclid.value.length + 1)) === '.' + fbclid.value;
      out.fbc = matchesCookie ? fbcCookie : 'fb.1.' + fbclid.ts + '.' + fbclid.value;
    } else if (fbcCookie) {
      out.fbc = fbcCookie;
    }

    var fbp = cookieValue('_fbp');
    if (fbp) out.fbp = fbp;

    var googleTs = latestTs(state, GOOGLE_KEYS);
    var metaTs = latestTs(state, META_KEYS);
    if (googleTs || metaTs) out.th_last_touch = googleTs >= metaTs ? 'google' : 'meta';

    return out;
  }

  function parseUrl(href) {
    try {
      return new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }
  }

  function isAppUrl(url) {
    var host = url.hostname.replace(/^www\./, '');
    return host === APP_HOST || host === FUNNEL_HOST;
  }

  function isFunnelUrl(url) {
    var host = url.hostname.replace(/^www\./, '');
    var path = url.pathname.replace(/\/$/, '') || '/';
    // A landing page is one segment: /start, or a campaign's /<slug>.
    if (host === FUNNEL_HOST) return /^\/[^/]+$/.test(path);
    if (path !== LEGACY_FUNNEL_PATH) return false;
    // Relative / same-origin hrefs accidentally pointing at the funnel path
    // count too.
    return host === APP_HOST || host === window.location.hostname.replace(/^www\./, '');
  }

  function buildHref(originalHref, state) {
    var url = parseUrl(originalHref);
    if (!url) return originalHref;
    var funnel = isFunnelUrl(url);
    if (!funnel && !isAppUrl(url)) return originalHref;

    if (funnel) {
      var defaults = {};
      UTM_KEYS.forEach(function (key) {
        var value = url.searchParams.get(key);
        if (value) defaults[key] = value;
        url.searchParams.delete(key);
      });

      // Placement used to ride in utm_content; it now has its own param so
      // the ad's utm_content survives.
      var placement = url.searchParams.get('th_cta') || defaults.utm_content;
      delete defaults.utm_content;
      if (placement) url.searchParams.set('th_cta', placement);

      var utm;
      if (state.utm && !state.utm.inferred) {
        utm = state.utm.params;
      } else if (state.utm) {
        utm = Object.assign({}, defaults, state.utm.params);
      } else {
        utm = defaults;
      }
      Object.keys(utm).forEach(function (key) {
        url.searchParams.set(key, utm[key]);
      });
    }

    var ids = clickParams(state);
    Object.keys(ids).forEach(function (key) {
      url.searchParams.set(key, ids[key]);
    });
    return url.toString();
  }

  var state = emptyState();

  // For scripts that send the visitor to the funnel themselves rather than
  // through a link (th-phone-signup.js): the landing's UTMs and the click ids,
  // as a funnel CTA carries them.
  window.thAttribution = {
    funnelParams: function () {
      var out = Object.assign({}, state.utm ? state.utm.params : {});
      return Object.assign(out, clickParams(state));
    },
  };

  function rewriteAnchor(anchor) {
    var original = anchor.getAttribute(ORIGINAL_HREF_ATTR);
    if (original === null) {
      original = anchor.getAttribute('href');
      var url = original && parseUrl(original);
      if (!url || !(isAppUrl(url) || isFunnelUrl(url))) return;
      anchor.setAttribute(ORIGINAL_HREF_ATTR, original);
    }
    anchor.setAttribute('href', buildHref(original, state));
  }

  function rewriteAnchors() {
    document.querySelectorAll('a[href]').forEach(rewriteAnchor);
  }

  function init() {
    state = readState();
    captureLanding(state);
    // Written every pageview so expired entries drop out and legacy keys get
    // migrated.
    writeState(state);
    rewriteAnchors();
  }

  // Rebuild right before navigation so _fbc/_fbp reflect whatever the Meta
  // pixel has set since page load, and links injected after init are covered.
  function onActivate(event) {
    var anchor = event.target.closest && event.target.closest('a[href]');
    if (anchor) rewriteAnchor(anchor);
  }
  document.addEventListener('click', onActivate, true);
  document.addEventListener('auxclick', onActivate, true);
  document.addEventListener('contextmenu', onActivate, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', rewriteAnchors);
})();
